import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { getSharedState } from "./windowBus";
import type { AppNotification } from "./types";

// Framework-agnostic (no dom.ts dependency, only a plain
// document.getElementById for the badge - see domBridge.ts's getVanillaEl
// for why that's safe but importing dom.ts itself isn't). Shared via
// windowBus since session.ts (app.js) and NotificationsDialog.tsx
// (react-app.js) both need the same cached list/badge state - see
// windowBus.ts.
interface NotificationsState {
  cached: AppNotification[];
  listeners: Set<() => void>;
}

function state(): NotificationsState {
  return getSharedState<NotificationsState>("__notificationsState", () => ({ cached: [], listeners: new Set() }));
}

function notify(): void {
  state().listeners.forEach((fn) => fn());
}

export function subscribeNotifications(fn: () => void): () => void {
  state().listeners.add(fn);
  return () => state().listeners.delete(fn);
}

export function getCachedNotifications(): AppNotification[] {
  return state().cached;
}

function updateBadge(count: number): void {
  const badge = document.getElementById("notificationsBadge");
  if (!badge) return;
  if (count > 0) {
    badge.textContent = count > 9 ? "9+" : String(count);
    badge.classList.remove("d-none");
  } else {
    badge.classList.add("d-none");
  }
}

export async function refreshNotifications(): Promise<void> {
  try {
    const res = await apiFetch("/api/notifications");
    if (res.status === 401) return;
    if (!res.ok) return;
    const data = await res.json();
    const s = state();
    s.cached = Array.isArray(data.notifications) ? data.notifications : [];
    const unread = typeof data.unreadCount === "number" ? data.unreadCount : s.cached.filter((n) => !n.read).length;
    updateBadge(unread);
    notify();
  } catch {
    // Best effort - the next protected-features refresh tick retries.
  }
}

// Pushed via the notification_created WS event (chatSocket.ts) - same shape
// GET /api/notifications already returns per-item, so this just unshifts
// rather than refetching the full list. Mirrors the server's own 50-item
// cap (server.js's usersDB.addNotification) so the cached list can't grow
// unbounded across a long session.
export function applyNotificationCreated(notification: AppNotification): void {
  const s = state();
  if (s.cached.some((n) => n.id === notification.id)) return;
  s.cached = [notification, ...s.cached].slice(0, 50);
  updateBadge(s.cached.filter((n) => !n.read).length);
  notify();
}

export async function markNotificationsRead(): Promise<void> {
  try {
    const res = await apiFetch("/api/notifications/read", { method: "POST" });
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const s = state();
    s.cached = s.cached.map((n) => ({ ...n, read: true }));
    updateBadge(0);
    notify();
  } catch {
    // Best effort - unread state just persists until the next successful call.
  }
}
